import { useCallback, useId, useImperativeHandle, useLayoutEffect, useRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes, type ReactNode, type KeyboardEvent, type MouseEvent, type Ref } from 'react';
import { clsx } from 'clsx';
import { Icon } from './icons';
import { IconButton } from './buttons';

// Painted padding and decorative slots belong to the field's hit target, without an extra tab stop.
function focusFieldControl(event: MouseEvent<HTMLDivElement>) {
  const interactive = event.target instanceof Element
    ? event.target.closest('input, select, textarea, button, a, label, [tabindex], [contenteditable]') : null;
  if (interactive && event.currentTarget.contains(interactive)) return;
  const control = event.currentTarget.querySelector<HTMLInputElement | HTMLSelectElement>('input, select');
  if (control && !control.disabled) control.focus();
}

interface FieldLabel { label: string; hideLabel?: boolean; hint?: string; error?: string; className?: string }
export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, FieldLabel { leading?: ReactNode; trailing?: ReactNode; ref?: Ref<HTMLInputElement> }
export function TextField({ label, hideLabel, hint, error, leading, trailing, className, id: givenId, ...props }: TextFieldProps) {
  const generatedId = useId(); const id = givenId ?? generatedId; const description = `${id}-description`;
  return <div className={clsx('hylo-field', className)}>
    <label htmlFor={id} className={hideLabel ? 'hylo-sr-only' : 'hylo-field__label'}>{label}</label>
    <div className="hylo-field__control" data-invalid={!!error} onClick={focusFieldControl}>{leading}<input {...props} id={id} aria-invalid={!!error || undefined}
      aria-describedby={[props['aria-describedby'], (error || hint) && description].filter(Boolean).join(' ') || undefined} />{trailing}</div>
    {(error || hint) && <div id={description} className={clsx('hylo-field__hint', error && 'is-error')}>{error || hint}</div>}
  </div>;
}

export interface SearchFieldProps extends Omit<TextFieldProps, 'type' | 'leading' | 'trailing'> { onClear?: () => void; clearLabel?: string; shortcut?: string }
export function SearchField({ onClear, clearLabel, shortcut, className, ...props }: SearchFieldProps) {
  return <TextField {...props} type="search" hideLabel={props.hideLabel ?? true} className={clsx('hylo-search', className)}
    leading={<Icon name="magnifyingglass" />} trailing={<>
      {onClear && !!props.value && <IconButton name="xmark.circle.fill" label={clearLabel ?? props.label} onClick={onClear} />}
      {shortcut && !props.value && <kbd aria-hidden="true">{shortcut}</kbd>}</>} />;
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FieldLabel { autoGrow?: boolean; maxRows?: number; monospaced?: boolean; ref?: Ref<HTMLTextAreaElement> }
export function TextArea({ label, hideLabel, hint, error, className, id: givenId, autoGrow = true, maxRows = 8, monospaced = false, onChange, ref: forwardedRef, ...props }: TextAreaProps) {
  const generatedId = useId(); const id = givenId ?? generatedId; const ref = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(forwardedRef, () => ref.current!, []);
  const resize = useCallback(() => {
    if (!autoGrow || !ref.current) return;
    const element = ref.current; element.style.height = 'auto';
    const computed = getComputedStyle(element); const line = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.35 || 23;
    const padding = (parseFloat(computed.paddingTop) || 0) + (parseFloat(computed.paddingBottom) || 0);
    const border = (parseFloat(computed.borderTopWidth) || 0) + (parseFloat(computed.borderBottomWidth) || 0);
    const limit = maxRows * line + padding; const height = Math.min(element.scrollHeight, limit) + border;
    const inset = computed.boxSizing === 'border-box' ? 0 : padding + border;
    element.style.height = `${Math.ceil(height - inset)}px`;
    element.style.overflowY = element.scrollHeight > limit ? 'auto' : 'hidden';
  }, [autoGrow, maxRows]);
  useLayoutEffect(resize, [props.value, props.defaultValue, props.rows, resize]);
  useLayoutEffect(() => {
    const element = ref.current; if (!autoGrow || !element) return;
    let frame = 0; let active = true;
    const schedule = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; if (active) resize(); }); };
    const geometry = () => {
      const style = getComputedStyle(element);
      return [element.clientWidth, style.fontFamily, style.fontSize, style.fontWeight, style.fontStyle, style.lineHeight,
        style.letterSpacing, style.wordSpacing, style.padding, style.borderWidth, style.boxSizing].join('|');
    };
    let signature = geometry();
    const check = () => { const next = geometry(); if (next !== signature) { signature = next; schedule(); } };
    // Ignore height-only notifications from our own measurements to avoid ResizeObserver feedback.
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(check); observer?.observe(element);
    const typography = new MutationObserver(check);
    for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) typography.observe(ancestor, { attributes: true });
    const fonts = document.fonts; fonts?.addEventListener('loadingdone', schedule);
    if (fonts?.status === 'loading') void fonts.ready.then(() => { if (active) schedule(); });
    window.addEventListener('resize', check);
    return () => {
      active = false; cancelAnimationFrame(frame); observer?.disconnect(); typography.disconnect();
      fonts?.removeEventListener('loadingdone', schedule); window.removeEventListener('resize', check);
    };
  }, [autoGrow, resize]);
  return <div className={clsx('hylo-field', className)}>
    <label htmlFor={id} className={hideLabel ? 'hylo-sr-only' : 'hylo-field__label'}>{label}</label>
    <textarea {...props} ref={ref} id={id} rows={props.rows ?? 3} className={clsx('hylo-textarea', monospaced && 'hylo-textarea--monospaced')} aria-invalid={!!error || undefined}
      aria-describedby={[props['aria-describedby'], (error || hint) && `${id}-description`].filter(Boolean).join(' ') || undefined}
      onChange={event => { resize(); onChange?.(event); }} />
    {(error || hint) && <div id={`${id}-description`} className={clsx('hylo-field__hint', error && 'is-error')}>{error || hint}</div>}
  </div>;
}

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange'> {
  label: string; hideLabel?: boolean; onCheckedChange?: (checked: boolean) => void;
  ref?: Ref<HTMLInputElement>;
}
export function Toggle({ label, hideLabel = false, onCheckedChange, className, ...props }: ToggleProps) {
  return <label className={clsx('hylo-toggle', className)}>
    <span className={hideLabel ? 'hylo-sr-only' : 'hylo-toggle__label'}>{label}</span>
    <span className="hylo-toggle__target"><input {...props} type="checkbox" role="switch" onChange={event => onCheckedChange?.(event.target.checked)} />
      <span className="hylo-toggle__track" aria-hidden="true"><span /></span></span>
  </label>;
}

export interface SegmentOption<T extends string> { value: T; label: string; icon?: string; disabled?: boolean }
export interface SegmentedControlProps<T extends string> {
  label: string; options: readonly SegmentOption<T>[]; value: T; onValueChange: (value: T) => void; className?: string; disabled?: boolean;
}
export function SegmentedControl<T extends string>({ label, options, value, onValueChange, className, disabled }: SegmentedControlProps<T>) {
  const choose = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
    const next = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? (rtl && event.key === 'ArrowRight' ? -1 : 1)
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? (rtl && event.key === 'ArrowLeft' ? 1 : -1) : 0;
    if (!next && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    const candidates = options.map((option, i) => ({ ...option, index: i })).filter(option => !option.disabled);
    const current = candidates.findIndex(option => option.index === index);
    const target = candidates[event.key === 'Home' ? 0 : event.key === 'End' ? candidates.length - 1 : (current + next + candidates.length) % candidates.length];
    if (target) { onValueChange(target.value); event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('button')[target.index]?.focus(); }
  };
  return <div role="radiogroup" aria-label={label} className={clsx('hylo-segmented', className)}>
    {options.map((option, i) => <button type="button" key={option.value} role="radio" aria-checked={value === option.value}
      tabIndex={value === option.value ? 0 : -1} disabled={disabled || option.disabled} onKeyDown={event => choose(event, i)}
      onClick={() => onValueChange(option.value)}>{option.icon && <Icon name={option.icon} />}{option.label}</button>)}
  </div>;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, FieldLabel {
  options?: readonly { value: string; label: string; disabled?: boolean }[];
  ref?: Ref<HTMLSelectElement>;
}
export function Select({ label, hideLabel, hint, error, options, children, id: givenId, className, ...props }: SelectProps) {
  const generatedId = useId(); const id = givenId ?? generatedId;
  return <div className={clsx('hylo-field', className)}><label htmlFor={id} className={hideLabel ? 'hylo-sr-only' : 'hylo-field__label'}>{label}</label>
    <div className="hylo-select" onClick={focusFieldControl}><select {...props} id={id} aria-invalid={!!error || undefined}
      aria-describedby={[props['aria-describedby'], (hint || error) && `${id}-description`].filter(Boolean).join(' ') || undefined}>
      {options?.map(option => <option key={option.value} {...option}>{option.label}</option>)}{children}</select><Icon name="chevron.up.chevron.down" /></div>
    {(error || hint) && <div id={`${id}-description`} className={clsx('hylo-field__hint', error && 'is-error')}>{error || hint}</div>}
  </div>;
}
export const Picker = Select;
